/**
 * Player API — Deliveries (shipping physical cards).
 *
 * These endpoints run inside the player's browser (your custom UI) and are
 * authorized with the player session token sent as the `X-Platform-Session`
 * request header (the PlatformSession security scheme). They cover the
 * shipping flow: manage delivery addresses, browse shipping options, get a
 * live fee quote, then create a delivery.
 *
 * IMPORTANT — off-chain charge: creating a delivery charges the player
 * immediately by calling YOUR `shipping_debit` wallet callback (see Wallet
 * Callbacks). On success the delivery is marked `paid` and the cards leave the
 * player's hands; a declined charge creates NO delivery. Fulfillment of
 * off-chain game-provider deliveries is handled manually (no carrier label).
 *
 * IMPORTANT — delivery queue limit: each operator has a cap on how many of ONE
 * player's cards may be awaiting fulfillment at the same time (default 100,
 * configurable per operator — ask us to change yours). Cards count while their
 * delivery is `paid` or `request_shipping`; the room frees up as deliveries
 * ship. `GET /pool_shippings/new` reports the player's current position
 * (`shipping_items_limit`, `items_in_processing`, `remaining_items`) so your UI
 * can cap the card picker, and creating a delivery beyond the cap is refused
 * with `422 SHIPPING_ITEM_LIMIT_REACHED`. It is a temporary state, not an
 * account restriction: tell the player their cards are being prepared and they
 * can request more once those ship.
 *
 * Each value below is an OpenAPI 3.1 path item. Failures return the shared
 * error envelope `{ error: 1, error_detail: { ... } }`.
 */

const ENVELOPE = { $ref: '#/components/schemas/ErrorEnvelope' };
const err = (description: string) => ({
  description,
  content: { 'application/json': { schema: ENVELOPE } },
});

const ADDRESS_PROPS = {
  id: { type: 'integer', description: 'Address id.' },
  name: { type: 'string', description: 'Recipient full name.' },
  phone_number: { type: 'string', description: 'Recipient phone.' },
  first_line: { type: 'string', description: 'Street address line 1.' },
  second_line: { type: 'string', nullable: true, description: 'Street address line 2.' },
  city: { type: 'string' },
  state: { type: 'string', nullable: true },
  country: { type: 'string', description: 'ISO country code.' },
  zipcode: { type: 'string' },
  additional: { type: 'string', nullable: true, description: 'Delivery instructions.' },
  is_default: { type: 'boolean', description: 'Whether this is the player default address.' },
} as const;

const ADDRESS_EXAMPLE = {
  id: 30,
  name: 'John Doe',
  phone_number: '+1 234 567 8900',
  first_line: '317 Railroad Avenue',
  second_line: null,
  city: 'Bedford Hills',
  state: 'NY',
  country: 'US',
  zipcode: '10507',
  additional: null,
  is_default: true,
} as const;

const ADDRESS_ID_PARAM = {
  name: 'id',
  in: 'path',
  required: true,
  schema: { type: 'integer' },
  description: 'Address id. Must belong to the current player.',
} as const;

const ADDRESS_RESPONSE_SCHEMA = {
  type: 'object',
  required: ['address'],
  properties: { address: { type: 'object', properties: ADDRESS_PROPS } },
} as const;

/** The promotion block of a charged delivery. The fee fields next to it are the
 *  price the player paid; this one holds the regular price they replaced. */
const CHARGE_PROMO_SCHEMA = {
  type: 'object',
  nullable: true,
  description:
    'Set when a shipping promotion lowered this delivery: `shipping`, `insurance`, `signature`, `handling` and `total` ' +
    'are then the price the player paid, and this block carries the regular fees they replaced and the discount you ' +
    'funded. `null` when no promotion lowered it.',
  properties: {
    id: { type: 'integer', description: 'Promotion id.' },
    label: { type: 'string', description: 'The promotion name.' },
    covers: {
      type: 'array',
      items: { type: 'string', enum: ['shipping', 'insurance', 'signature', 'handling'] },
      description: 'The parts of the price the promotion covered. A part it did not cover was paid at its regular price.',
    },
    original_shipping: { type: 'number', description: 'Regular `shipping` without the promotion, add-ons included.' },
    original_insurance: { type: 'number', nullable: true, description: 'Regular insurance add-on alone; `0` when the option has none.' },
    original_signature: { type: 'number', nullable: true, description: 'Regular signature add-on alone; `0` when the option has none.' },
    original_handling: { type: 'number', description: 'Regular `handling` without the promotion.' },
    discount: {
      type: 'number',
      description: 'What the promotion took off the regular shipping and handling fees — the amount you fund.',
    },
  },
} as const;

/** `charge_breakdown` is shared by Get a delivery and Create a delivery. */
const CHARGE_BREAKDOWN_SCHEMA = {
  type: 'object',
  nullable: true,
  description:
    'What the player actually paid when this delivery was charged (shipping_debit), in your currency. `null` until the delivery has been charged.',
  properties: {
    shipping: { type: 'number', description: 'Includes the chosen insurance/signature option add-on.' },
    insurance: { type: 'number', nullable: true, description: 'Insurance portion of `shipping` (which stays the full option price). `null` for deliveries charged before the breakdown existed.' },
    signature: { type: 'number', nullable: true, description: 'Signature portion of `shipping`. `null` for deliveries charged before the breakdown existed.' },
    handling: { type: 'number' },
    sales_tax: { type: 'number', nullable: true, description: 'Sales tax charged with this delivery; `0`/`null` for operators without sales tax enabled.' },
    total: { type: 'number' },
    promo: CHARGE_PROMO_SCHEMA,
  },
} as const;

const CHARGE_BREAKDOWN_EXAMPLE = { shipping: 9.92, insurance: 2.5, signature: 1.45, handling: 3.0, sales_tax: null, total: 12.92, promo: null } as const;

/** The same delivery under a $1 shipping + handling promotion: the add-ons stay at their regular price, on top. */
const CHARGE_BREAKDOWN_PROMO_EXAMPLE = {
  shipping: 3.95,
  insurance: 2.5,
  signature: 1.45,
  handling: 1.0,
  sales_tax: null,
  total: 4.95,
  promo: {
    id: 12,
    label: 'November shipping promo',
    covers: ['shipping', 'handling'],
    original_shipping: 9.92,
    original_insurance: 2.5,
    original_signature: 1.45,
    original_handling: 3.0,
    discount: 7.97,
  },
} as const;

/** A paid delivery as Get a delivery returns it, minus the charge breakdown that each example brings. */
const DELIVERY_EXAMPLE = {
  id: 4012,
  status: 'paid',
  is_allow_to_cancel: true,
  address: ADDRESS_EXAMPLE,
  pool_items: [{ id: 778812, title: 'Conor McGregor 2021 Prizm #112', images: [{ url: 'https://cdn.example.com/blobs/card.png', metadata: {}, signed_id: 'abc123' }] }],
  pool_shipping_positions: [{ id: 9001, name: 'Add shipping insurance and signature on delivery', price: 9.92 }],
  operator_currency: 'USD',
  item_settle_statuses: { '778812': 'shipped' },
  settle_summary: null,
} as const;

const SERVICE_PROPS = {
  id: { type: 'integer', description: 'Shipping service (option) id.' },
  name: { type: 'string', description: 'Option name, e.g. "Add shipping insurance and signature on delivery".' },
  description: { type: 'string', nullable: true },
  price: { type: 'number', nullable: true, description: 'Flat catalogue price in USD; the live per-option price comes from the fees endpoint.' },
  code_name: { type: 'string', enum: ['delivery', 'insurance', 'signature', 'insurance_and_signature'], description: 'What the option includes. `required_code_name` in the fee quote names one of these.' },
} as const;

export const shippingsPaths: Record<string, unknown> = {
  // ─── Addresses ──────────────────────────────────────────────────────────
  '/loot_machine/api/game_platform/iframe/v1/addresses': {
    get: {
      tags: ['Deliveries'],
      summary: 'List delivery addresses',
      description: [
        "The player's saved shipping addresses, the default first.",
        '',
        'Use the default (or let the player switch) when creating a delivery.',
      ].join('\n'),
      operationId: 'listPlayerAddresses',
      security: [{ PlatformSession: [] }],
      responses: {
        '200': {
          description: 'The saved shipping addresses.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['addresses'],
                properties: {
                  addresses: { type: 'array', items: { type: 'object', title: 'Address', properties: ADDRESS_PROPS } },
                },
              },
              example: { addresses: [ADDRESS_EXAMPLE] },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '500': err('Unexpected error.'),
      },
    },
    post: {
      tags: ['Deliveries'],
      summary: 'Create a delivery address',
      description: [
        'Add a shipping address for the player. A newly created address becomes the default.',
      ].join('\n'),
      operationId: 'createPlayerAddress',
      security: [{ PlatformSession: [] }],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              required: ['address'],
              properties: {
                address: {
                  type: 'object',
                  required: ['name', 'phone_number', 'first_line', 'city', 'country', 'zipcode'],
                  properties: {
                    name: { type: 'string' },
                    phone_number: { type: 'string' },
                    first_line: { type: 'string' },
                    second_line: { type: 'string' },
                    city: { type: 'string' },
                    state: { type: 'string' },
                    country: { type: 'string' },
                    zipcode: { type: 'string' },
                    additional: { type: 'string' },
                  },
                },
              },
            },
            example: {
              address: {
                name: 'John Doe',
                phone_number: '+1 234 567 8900',
                first_line: '317 Railroad Avenue',
                city: 'Bedford Hills',
                state: 'NY',
                country: 'US',
                zipcode: '10507',
              },
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'The created address.',
          content: {
            'application/json': {
              schema: { type: 'object', required: ['address'], properties: { address: { type: 'object', properties: ADDRESS_PROPS } } },
              example: { address: ADDRESS_EXAMPLE },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '422': err('The address could not be saved (validation error).'),
        '500': err('Unexpected error.'),
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/addresses/{id}': {
    get: {
      tags: ['Deliveries'],
      summary: 'Get a delivery address',
      operationId: 'getPlayerAddress',
      security: [{ PlatformSession: [] }],
      parameters: [ADDRESS_ID_PARAM],
      responses: {
        '200': {
          description: 'The address.',
          content: { 'application/json': { schema: ADDRESS_RESPONSE_SCHEMA, example: { address: ADDRESS_EXAMPLE } } },
        },
        '401': err('Missing or invalid player session token.'),
        '404': err('Address not found or not owned by the current player.'),
        '500': err('Unexpected error.'),
      },
    },
    put: {
      tags: ['Deliveries'],
      summary: 'Update a delivery address',
      description: [
        'Change any subset of the address fields; omitted fields keep their value.',
        '',
        'Send `is_default: true` to also make it the player default (clears the previous default). `is_default: false` is ignored — the player always keeps a default; make another address the default instead.',
      ].join('\n'),
      operationId: 'updatePlayerAddress',
      security: [{ PlatformSession: [] }],
      parameters: [ADDRESS_ID_PARAM],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              required: ['address'],
              properties: {
                address: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    phone_number: { type: 'string' },
                    first_line: { type: 'string' },
                    second_line: { type: 'string' },
                    city: { type: 'string' },
                    state: { type: 'string' },
                    country: { type: 'string' },
                    zipcode: { type: 'string' },
                    additional: { type: 'string' },
                    is_default: { type: 'boolean' },
                  },
                },
              },
            },
            example: { address: { first_line: '12 Main Street', is_default: true } },
          },
        },
      },
      responses: {
        '200': {
          description: 'The updated address.',
          content: { 'application/json': { schema: ADDRESS_RESPONSE_SCHEMA, example: { address: ADDRESS_EXAMPLE } } },
        },
        '401': err('Missing or invalid player session token.'),
        '404': err('Address not found or not owned by the current player.'),
        '422': err('The address could not be saved (validation error).'),
        '500': err('Unexpected error.'),
      },
    },
    delete: {
      tags: ['Deliveries'],
      summary: 'Delete a delivery address',
      description: [
        'Remove a saved shipping address.',
        '',
        'If it was the default, the most recently added remaining address becomes the default. Deleting the last address leaves the player with none.',
        '',
        'An address used by a delivery that is not delivered or canceled yet (`pending`, `waiting_for_payment`, `paid`, `shipped`, `request_shipping`) cannot be deleted: the request is refused with `422 ADDRESS_IN_USE` and nothing changes.',
      ].join('\n'),
      operationId: 'deletePlayerAddress',
      security: [{ PlatformSession: [] }],
      parameters: [ADDRESS_ID_PARAM],
      responses: {
        '200': {
          description: 'The address was deleted.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['result'],
                properties: {
                  result: { type: 'object', required: ['deleted'], properties: { deleted: { type: 'boolean', const: true } } },
                },
              },
              example: { result: { deleted: true } },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '404': err('Address not found or not owned by the current player.'),
        '422': {
          description: '`ADDRESS_IN_USE` — a delivery in progress still uses this address.',
          content: {
            'application/json': {
              schema: ENVELOPE,
              example: { error: 1, error_detail: { code: 'ADDRESS_IN_USE', message: 'This address is used by a delivery in progress' } },
            },
          },
        },
        '500': err('Unexpected error.'),
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/addresses/{id}/default': {
    put: {
      tags: ['Deliveries'],
      summary: 'Set the default address',
      description: 'Make this the player default shipping address (clears the previous default).',
      operationId: 'setDefaultPlayerAddress',
      security: [{ PlatformSession: [] }],
      parameters: [{ name: 'id', in: 'path', required: true, schema: { type: 'integer' }, description: 'Address id. Must belong to the current player.' }],
      responses: {
        '200': {
          description: 'The address, now the default.',
          content: { 'application/json': { schema: { type: 'object', required: ['address'], properties: { address: { type: 'object', properties: ADDRESS_PROPS } } }, example: { address: ADDRESS_EXAMPLE } } },
        },
        '401': err('Missing or invalid player session token.'),
        '404': err('Address not found or not owned by the current player.'),
        '500': err('Unexpected error.'),
      },
    },
  },

  // ─── Shipping options + quote ───────────────────────────────────────────
  '/loot_machine/api/game_platform/iframe/v1/pool_shippings/new': {
    get: {
      tags: ['Deliveries'],
      summary: 'List shipping options',
      description: [
        'The Delivery Shipping Options catalogue the player chooses from (insurance /',
        'signature combos). Live per-option prices come from the fees endpoint.',
        '',
        'The response also reports the player\'s delivery-queue position, so the UI can',
        'cap the card picker before the player builds a basket we would refuse:',
        '',
        '- `shipping_items_limit` — your operator cap on cards awaiting fulfillment at once.',
        '- `items_in_processing` — cards of this player already awaiting fulfillment.',
        '- `remaining_items` — how many cards may be added right now (never negative).',
        '',
        'When `remaining_items` is `0`, show the player a "delivery queue is full" notice',
        'instead of the form; creating a delivery anyway returns',
        '`422 SHIPPING_ITEM_LIMIT_REACHED`.',
      ].join('\n'),
      operationId: 'listShippingOptions',
      security: [{ PlatformSession: [] }],
      responses: {
        '200': {
          description: 'The shipping option catalogue and the player delivery-queue position.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    properties: {
                      services: { type: 'array', items: { type: 'object', title: 'ShippingOption', properties: SERVICE_PROPS } },
                      shipping_items_limit: { type: 'integer', description: 'Operator cap on this player\'s cards awaiting fulfillment at once.' },
                      items_in_processing: { type: 'integer', description: 'Cards of this player already awaiting fulfillment (`paid` / `request_shipping` deliveries).' },
                      remaining_items: { type: 'integer', description: 'Cards the player may still add to a new delivery. `0` = queue full.' },
                    },
                  },
                },
              },
              example: {
                result: {
                  services: [
                    { id: 30, name: 'No insurance and no signature', description: null, price: null, code_name: 'delivery' },
                    { id: 31, name: 'Add shipping insurance and signature on delivery', description: null, price: null, code_name: 'insurance_and_signature' },
                  ],
                  shipping_items_limit: 100,
                  items_in_processing: 42,
                  remaining_items: 58,
                },
              },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '500': err('Unexpected error.'),
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_shippings/fees': {
    get: {
      tags: ['Deliveries'],
      summary: 'Quote shipping fees',
      description: [
        'Live fee quote for a potential delivery — what the player sees before confirming.',
        '',
        'Pass the cards (`pool_item_ids[]`), the delivery `address_id`, and the chosen',
        'option(s) (`pool_shipping_service_ids[]`). Shipping is quoted live from EasyPost',
        '(cheapest USPS rate + the option add-on); handling is a flat fee. `option_prices`',
        'gives the full price per option id so the UI can label every choice; `note` is the',
        'auto-buyback warning copy. `shipping_fee_components` splits `shipping_fee` into its',
        'base / insurance / signature parts so the UI can show insurance as its own line.',
        '`sales_tax` is quoted at the delivery address only for operators with sales tax',
        'enabled (see *Deliveries & shipping → Sales tax*); show it as its own line. `total` is',
        'the amount to charge — `shipping_fee + handling_fee + sales_tax` — and is `null` while',
        '`shipping_fee` is.',
        '',
        'While a shipping promotion is active for your platform, `shipping_fee`,',
        '`shipping_fee_components`, `handling_fee`, `option_prices` and `total` already hold the',
        'price the player pays. `promo.covers` says which parts of the price it lowers: the discount',
        'comes off those parts only, and a part it does not cover stays at its regular price, on top.',
        '`promo` also carries the regular fees (`original_*`) and the `discount` you fund, so your UI',
        'can show a struck-through price; it is `null` when the promotion lowers neither this delivery',
        'nor any of its options. `discount` is `0` when it lowers only other options, or when no option',
        'is chosen yet and handling is not covered. The price is fixed when the delivery is created, not',
        'when it is quoted.',
        '',
        '`protection` says which add-ons this delivery requires: insurance from 6 cards or $250 of total card',
        'value, signature from 11 cards or above $500. Preselect the option whose `code_name` is',
        '`required_code_name` (see *List shipping options*). The player may pick a lower option, and a delivery',
        'below what is required then needs `acknowledged_opt_out` on *Create a delivery*.',
        '',
        'A `200` is a usable quote: figures that simply aren\'t known yet — `shipping_fee`',
        'and `option_prices` before an `address_id` + option are supplied — come back `null`',
        '/ empty ("TBD"). A rating FAILURE is never a `200` with null shipping; it is one of',
        'two explicit HTTP errors so the UI can react correctly:',
        '',
        '- **503 `SHIPPING_RATING_UNAVAILABLE`** — the upstream rating provider (EasyPost)',
        '  timed out or errored. Transient, not the player\'s fault: retry the quote with',
        '  backoff (a few tries, a few seconds apart).',
        '- **422 `SHIPPING_UNRATEABLE_ADDRESS`** — the provider answered but no shipping',
        '  rate exists for this address. Do NOT retry the same address; prompt the player',
        '  to check and correct it.',
        '',
        'Tip: reuse a stable `address_id` instead of creating a new address per quote —',
        'successful quotes are cached briefly per address, so reuse avoids re-hitting the',
        'rating provider (and the transient 503s that come with it).',
      ].join('\n'),
      operationId: 'quoteShippingFees',
      security: [{ PlatformSession: [] }],
      parameters: [
        { name: 'pool_item_ids[]', in: 'query', required: false, schema: { type: 'array', items: { type: 'integer' } }, description: 'Cards to ship (repeat the param per id).' },
        { name: 'address_id', in: 'query', required: false, schema: { type: 'integer' }, description: 'Delivery address — unlocks the shipping + sales-tax quote.' },
        { name: 'pool_shipping_service_ids[]', in: 'query', required: false, schema: { type: 'array', items: { type: 'integer' } }, description: 'Chosen shipping option(s).' },
      ],
      responses: {
        '200': {
          description: 'A usable fee quote. Null / empty figures are simply not quotable yet (no address / no items) — a rating failure is a 422 or 503, never a 200 with null shipping.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    properties: {
                      shipping_fee: { type: 'number', nullable: true, description: 'Selected option full price, in your currency; null until items + address + an option exist.' },
                      shipping_fee_components: {
                        type: 'object',
                        nullable: true,
                        description: 'Breakdown of `shipping_fee` for the selected option; `base + insurance + signature` equals `shipping_fee`. Null exactly when `shipping_fee` is null (not quotable yet).',
                        properties: {
                          base: { type: 'number', description: 'Carrier rate, in your currency.' },
                          insurance: { type: 'number', description: 'Declared-value insurance for the selected option, in your currency; 0 when the option has no insurance.' },
                          signature: { type: 'number', description: 'Signature-confirmation surcharge, in your currency; 0 when the option has no signature confirmation.' },
                        },
                      },
                      handling_fee: { type: 'number', nullable: true, description: 'Flat handling charge, in your currency.' },
                      sales_tax: { type: 'number', nullable: true, description: 'Sales tax, in your currency, at the delivery address, quoted only for operators with sales tax enabled (the taxable base — card value and/or the fees — is configured per operator by Repackz). `null` without an address or when sales tax is not enabled. Included in `total` on the delivery and in the shipping debit; refunds return each card\'s share.' },
                      option_prices: { type: 'object', additionalProperties: { type: 'number' }, description: 'Full shipping price per option id, handling not included, e.g. { "30": 5.97, "31": 9.92 }. The difference between two options is what an add-on costs the player.' },
                      total: { type: 'number', nullable: true, description: 'The amount to charge the player, in your currency: `shipping_fee + handling_fee + sales_tax`. `null` while `shipping_fee` is null.' },
                      promo: {
                        type: 'object',
                        nullable: true,
                        description: 'Set while a shipping promotion lowers this delivery or one of its options: the figures above are then the price the player pays, and this block carries the regular price they replace. `null` when no promotion lowers any of them.',
                        properties: {
                          id: { type: 'integer', description: 'Promotion id.' },
                          label: { type: 'string', description: 'The promotion name.' },
                          covers: {
                            type: 'array',
                            items: { type: 'string', enum: ['shipping', 'insurance', 'signature', 'handling'] },
                            description: 'The parts of the price it covers: `shipping` is the carrier rate (`base`), `insurance` and `signature` the add-ons, `handling` the handling fee. A part it does not cover stays at its regular price.',
                          },
                          starts_at: { type: 'string', format: 'date-time', description: 'When the promotion starts (ISO 8601, UTC).' },
                          ends_at: { type: 'string', format: 'date-time', description: 'When the promotion ends (ISO 8601, UTC); the instant itself is already outside it.' },
                          original_shipping_fee: { type: 'number', nullable: true, description: 'Regular `shipping_fee` without the promotion, in your currency; `null` while `shipping_fee` is.' },
                          original_shipping_fee_components: {
                            type: 'object',
                            nullable: true,
                            description: 'Regular `shipping_fee_components`; `null` while `shipping_fee` is.',
                            properties: {
                              base: { type: 'number' },
                              insurance: { type: 'number' },
                              signature: { type: 'number' },
                            },
                          },
                          original_handling_fee: { type: 'number', description: 'Regular `handling_fee` without the promotion, in your currency.' },
                          original_option_prices: { type: 'object', additionalProperties: { type: 'number' }, description: 'Regular price per shipping option id — what `option_prices` would be without the promotion.' },
                          discount: { type: 'number', description: 'What the promotion takes off this quote, the chosen option and the handling, in your currency — the amount you fund. `0` when it lowers only other options, or when no option is chosen yet and handling is not covered.' },
                        },
                      },
                      protection: {
                        type: 'object',
                        nullable: true,
                        description: 'The add-ons this delivery requires, from its card count and total card value.',
                        properties: {
                          required_insurance: { type: 'boolean', description: 'Insurance is required: 6 cards or more, or $250 of total card value or more.' },
                          required_signature: { type: 'boolean', description: 'Signature is required: 11 cards or more, or above $500 of total card value.' },
                          required_code_name: { type: 'string', enum: ['delivery', 'insurance', 'insurance_and_signature'], description: 'The `code_name` of the option to preselect (see *List shipping options*).' },
                          insurance_reason: { type: 'array', nullable: true, items: { type: 'string', enum: ['card_count', 'fmv'] }, description: 'Why insurance is required; `null` when it is not.' },
                          signature_reason: { type: 'array', nullable: true, items: { type: 'string', enum: ['card_count', 'fmv'] }, description: 'Why signature is required; `null` when it is not.' },
                          single_card_over_max: { type: 'boolean', description: 'One card is worth more than `max_insurable_value`, so it cannot be fully insured.' },
                          insurance_capped: { type: 'boolean', description: 'The cards together are worth more than `max_insurable_value`, so coverage stops there.' },
                          max_insurable_value: { type: 'number', description: 'The most insurance covers, in USD.' },
                        },
                      },
                      note: { type: 'string', nullable: true, description: 'Confirm-by / auto-buyback warning copy.' },
                    },
                  },
                },
              },
              examples: {
                regular: {
                  summary: 'No promotion',
                  value: { result: { shipping_fee: 9.92, shipping_fee_components: { base: 5.97, insurance: 2.5, signature: 1.45 }, handling_fee: 3.0, sales_tax: null, option_prices: { '30': 5.97, '31': 9.92 }, total: 12.92, promo: null, note: 'If you save items for shipping, you must confirm shipping before the 24-hour limit expires. After 24 hours, the item will be automatically bought back at 90% of FMV (Fair Market Value).' } },
                },
                promotion: {
                  summary: 'Priced by a $1 shipping + handling promotion',
                  value: {
                    result: {
                      shipping_fee: 3.95,
                      shipping_fee_components: { base: 0, insurance: 2.5, signature: 1.45 },
                      handling_fee: 1.0,
                      sales_tax: null,
                      option_prices: { '30': 0, '31': 3.95 },
                      total: 4.95,
                      promo: {
                        id: 12,
                        label: 'November shipping promo',
                        covers: ['shipping', 'handling'],
                        starts_at: '2026-11-01T04:00:00Z',
                        ends_at: '2026-12-01T05:00:00Z',
                        original_shipping_fee: 9.92,
                        original_shipping_fee_components: { base: 5.97, insurance: 2.5, signature: 1.45 },
                        original_handling_fee: 3.0,
                        original_option_prices: { '30': 5.97, '31': 9.92 },
                        discount: 7.97,
                      },
                      note: 'If you save items for shipping, you must confirm shipping before the 24-hour limit expires. After 24 hours, the item will be automatically bought back at 90% of FMV (Fair Market Value).',
                    },
                  },
                },
              },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '422': err('`SHIPPING_UNRATEABLE_ADDRESS` — the address is valid but no shipping rate could be produced for it. Do NOT retry; ask the player to check and correct the address.'),
        '500': err('Unexpected error.'),
        '503': err('`SHIPPING_RATING_UNAVAILABLE` — the upstream rating provider (EasyPost) timed out or errored. Transient: retry the quote with backoff.'),
      },
    },
  },

  // ─── Deliveries ─────────────────────────────────────────────────────────
  '/loot_machine/api/game_platform/iframe/v1/pool_shippings': {
    get: {
      tags: ['Deliveries'],
      summary: 'List deliveries',
      description: "The player's deliveries, newest first.",
      operationId: 'listDeliveries',
      security: [{ PlatformSession: [] }],
      parameters: [
        { name: 'page', in: 'query', required: false, schema: { type: 'integer', default: 1 } },
        { name: 'per', in: 'query', required: false, schema: { type: 'integer' } },
      ],
      responses: {
        '200': {
          description: 'A page of deliveries.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_shippings'],
                properties: {
                  pool_shippings: {
                    type: 'array',
                    items: {
                      type: 'object',
                      title: 'DeliveryRow',
                      properties: {
                        id: { type: 'integer' },
                        status: { type: 'string', enum: ['pending', 'waiting_for_payment', 'paid', 'request_shipping', 'shipped', 'delivered', 'canceled'], description: 'Delivery status. `request_shipping` = fulfillment has started (between `paid` and `shipped`).' },
                        is_allow_to_cancel: { type: 'boolean', description: 'True only while the delivery is `paid` and still has unresolved cards — i.e. it can still be canceled (see Cancel a delivery).' },
                        humanized_status: { type: 'string' },
                        pool_items_count: { type: 'integer' },
                        created_at: { type: 'string', format: 'date-time', nullable: true },
                        preview_image_url: { type: 'string', format: 'uri', nullable: true, description: "First item's image, for the list card." },
                      },
                    },
                  },
                  meta: { type: 'object', properties: { total_count: { type: 'integer' }, page: { type: 'integer' }, per: { type: 'integer' } } },
                },
              },
              example: { pool_shippings: [{ id: 4012, status: 'paid', is_allow_to_cancel: true, humanized_status: 'Paid', pool_items_count: 1, created_at: '2026-06-04T10:00:00Z', preview_image_url: 'https://cdn.example.com/blobs/card.png' }], meta: { total_count: 1, page: 1, per: 20 } },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '500': err('Unexpected error.'),
      },
    },
    post: {
      tags: ['Deliveries'],
      summary: 'Create a delivery (charges the player)',
      description: [
        'Create a delivery for the chosen cards + address + shipping option, and charge',
        'the player immediately.',
        '',
        'For off-chain game-provider items this calls YOUR `shipping_debit` wallet',
        'callback to debit the shipping cost (shipping + handling + sales tax, if enabled). While a',
        'shipping promotion is active, the amount the player owes — and the `shipping_debit`, where one',
        'is sent — is the promotional price; the price is fixed at this call, so `charge_breakdown`',
        'on the response is what was actually charged. On success the',
        'delivery is created `paid` and the cards are taken out of the player\'s hands',
        '(redeemed for physical fulfillment). If your wallet declines the charge NO delivery is created: an',
        'empty balance is answered with `422 INSUFFICIENT_BALANCE`, any other declined charge with',
        '`402 SHIPPING_CHARGE_FAILED`.',
        '',
        '**Guard against a price change.** Send the total the player was shown as `expected_total` (a JSON',
        'number in your currency — the `total` of the fee quote). If the price has risen by the time the',
        'delivery is created — a promotion ended, or the rates moved — the request is refused with',
        '`409 SHIPPING_PRICE_CHANGED`: nothing is charged and the cards go back to the player. Quote again and',
        'ask the player to confirm the new total. A price that stayed the same or fell is charged as it stands.',
        'We recommend that API-only operators, who charge the player themselves, always send it. A value',
        'that is not a non-negative number is refused with `422 INVALID_EXPECTED_TOTAL`.',
        '',
        'Fulfillment is handled manually — no carrier label is bought for off-chain items.',
        '',
        'The delivery-queue cap is enforced here: if the cards in this request do not fit',
        'the player\'s `remaining_items` (see "List shipping options"), the request is',
        'refused with `422 SHIPPING_ITEM_LIMIT_REACHED` BEFORE anything is charged or',
        'created, and the message states how many cards are in progress and how many can',
        'still be added. This can also happen after a successful pre-check if the player',
        'created another delivery in a second tab meanwhile — handle the code, don\'t rely',
        'on the pre-check alone.',
      ].join('\n'),
      operationId: 'createDelivery',
      security: [{ PlatformSession: [] }],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              required: ['pool_shipping'],
              properties: {
                pool_shipping: {
                  type: 'object',
                  required: ['address_id'],
                  properties: {
                    address_id: { type: 'integer', description: 'Delivery address id.' },
                    pool_shipping_items_attributes: {
                      type: 'array',
                      description: 'The cards to ship.',
                      items: { type: 'object', properties: { pool_item_id: { type: 'integer' } } },
                    },
                    pool_shipping_positions_attributes: {
                      type: 'array',
                      description: 'The chosen shipping option(s).',
                      items: { type: 'object', properties: { pool_shipping_service_id: { type: 'integer' } } },
                    },
                  },
                },
                acknowledged_opt_out: {
                  type: 'boolean',
                  description:
                    'Send `true` when the player confirmed going without an add-on the quote\'s `protection` requires. Without it such a ' +
                    'delivery is refused with `422 PROTECTION_ACK_REQUIRED` and nothing is charged.',
                },
                expected_total: {
                  type: 'number',
                  minimum: 0,
                  description:
                    'The total the player was shown, in your currency (the `total` of the fee quote). Optional but recommended: if ' +
                    'the price has risen by the time the delivery is created the request is refused with `409 SHIPPING_PRICE_CHANGED` and nothing is charged. A price that ' +
                    'stayed the same or fell is charged as it stands.',
                },
              },
            },
            example: { pool_shipping: { address_id: 30, pool_shipping_items_attributes: [{ pool_item_id: 778812 }], pool_shipping_positions_attributes: [{ pool_shipping_service_id: 31 }] }, expected_total: 12.92 },
          },
        },
      },
      responses: {
        '200': {
          description: 'Delivery created and charged. Status is `paid`. The body is the same delivery detail as *Get a delivery* — only the fields that matter at checkout are listed here; read `charge_breakdown` for what was charged.',
          content: {
            'application/json': {
              schema: { type: 'object', required: ['pool_shipping'], properties: { pool_shipping: { type: 'object', properties: { id: { type: 'integer' }, status: { type: 'string', example: 'paid' }, charge_breakdown: CHARGE_BREAKDOWN_SCHEMA } } } },
              examples: {
                regular: { summary: 'No promotion', value: { pool_shipping: { id: 4012, status: 'paid', charge_breakdown: CHARGE_BREAKDOWN_EXAMPLE } } },
                promotion: { summary: 'Priced by a $1 shipping + handling promotion', value: { pool_shipping: { id: 4012, status: 'paid', charge_breakdown: CHARGE_BREAKDOWN_PROMO_EXAMPLE } } },
              },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '402': err('`SHIPPING_CHARGE_FAILED` — the shipping charge was declined for a reason other than an empty balance. No delivery was created.'),
        '409': err('`SHIPPING_PRICE_CHANGED` — the total is now higher than the `expected_total` you sent (for example a promotion ended). Nothing was charged and the cards are back with the player. Fetch a new fee quote and ask the player to confirm the new total.'),
        '422': err('The delivery could not be created — `INSUFFICIENT_BALANCE` when the player\'s balance does not cover the charge, a validation error, `PROTECTION_ACK_REQUIRED` when the chosen option drops an add-on the quote\'s `protection` requires and `acknowledged_opt_out` is not `true`, `INVALID_EXPECTED_TOTAL` when `expected_total` is not a non-negative number, or SHIPPING_ITEM_LIMIT_REACHED when the player already has the operator maximum of cards awaiting fulfillment. Nothing was charged.'),
        '500': err('Unexpected error.'),
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_shippings/{id}': {
    get: {
      tags: ['Deliveries'],
      summary: 'Get a delivery',
      description: 'A single delivery belonging to the current player — its address, items and chosen options.',
      operationId: 'getDelivery',
      security: [{ PlatformSession: [] }],
      parameters: [{ name: 'id', in: 'path', required: true, schema: { type: 'integer' }, description: 'Delivery id. Must belong to the current player.' }],
      responses: {
        '200': {
          description: 'The delivery detail.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['pool_shipping'],
                properties: {
                  pool_shipping: {
                    type: 'object',
                    properties: {
                      id: { type: 'integer' },
                      status: { type: 'string', enum: ['pending', 'waiting_for_payment', 'paid', 'request_shipping', 'shipped', 'delivered', 'canceled'], description: 'Delivery status. `request_shipping` = fulfillment has started (between `paid` and `shipped`).' },
                      is_allow_to_cancel: { type: 'boolean', description: 'True only while the delivery is `paid` and still has unresolved (`pending`) cards. Once fulfillment starts (`request_shipping` and later) or every card is already resolved, this flips to false and a cancel returns `SHIPPING_NOT_CANCELABLE`.' },
                      is_can_cancel: { type: 'boolean', description: 'Legacy web-flow flag (unpaid deliveries only) — still present in the payload; gate cancellation UI on `is_allow_to_cancel` instead.' },
                      address: { type: 'object', nullable: true, properties: ADDRESS_PROPS },
                      pool_items: { type: 'array', items: { type: 'object', properties: { id: { type: 'integer' }, title: { type: 'string' }, images: { type: 'array', items: { $ref: '#/components/schemas/ImageObject' } } } } },
                      pool_shipping_positions: { type: 'array', items: { type: 'object', properties: { id: { type: 'integer' }, name: { type: 'string' }, price: { type: 'number', nullable: true } } } },
                      operator_currency: { type: 'string', nullable: true, description: 'Your settlement currency code, as configured on your operator account (not necessarily ISO-4217).' },
                      charge_breakdown: CHARGE_BREAKDOWN_SCHEMA,
                      item_settle_statuses: {
                        type: 'object',
                        additionalProperties: { type: 'string', enum: ['pending', 'shipped', 'unavailable', 'canceled'] },
                        description: 'Per-card fulfillment status keyed by pool_item id. `pending`: not yet resolved. `shipped`: physically fulfilled. `unavailable`: could not be delivered (damaged/lost) — bought back and refunded, see Wallet Callbacks → Credit and Refund. `canceled`: the player canceled the delivery — the card was bought back at the regular buyback price.',
                      },
                      settle_summary: {
                        type: 'string',
                        nullable: true,
                        enum: ['refunded', 'partially_shipped'],
                        description: '`refunded` if every card in this delivery ended up unavailable (fully bought back + refunded). `partially_shipped` if some shipped and some were unavailable. `null` while nothing has been marked unavailable — either still in progress, or fully shipped normally.',
                      },
                    },
                  },
                },
              },
              examples: {
                regular: { summary: 'No promotion', value: { pool_shipping: { ...DELIVERY_EXAMPLE, charge_breakdown: CHARGE_BREAKDOWN_EXAMPLE } } },
                promotion: {
                  summary: 'Priced by a $1 shipping + handling promotion',
                  value: { pool_shipping: { ...DELIVERY_EXAMPLE, charge_breakdown: CHARGE_BREAKDOWN_PROMO_EXAMPLE } },
                },
              },
            },
          },
        },
        '401': err('Missing or invalid player session token.'),
        '404': err('Delivery not found or not owned by the current player.'),
        '500': err('Unexpected error.'),
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_shippings/{id}/cancel': {
    put: {
      tags: ['Deliveries'],
      summary: 'Cancel a delivery',
      description: [
        'Cancel a delivery that has not started fulfillment.',
        '',
        'A **`paid`** delivery is fully unwound: every card is bought back at the regular',
        'buyback price (`fair_market_value × buyback_percent` — a `credit` wallet callback',
        'per card) and the shipping + handling charge, sales tax included, is refunded to the',
        'wallet (`refund` callback). The cards leave the player as sold; the delivery becomes `canceled`.',
        'A not-yet-charged delivery is simply canceled (a fresh buyback offer is re-issued',
        'for each card).',
        '',
        'The window closes once fulfillment starts: from `request_shipping` onward the',
        'delivery is no longer cancelable and the call returns `422 SHIPPING_NOT_CANCELABLE`.',
        'Gate your UI on the `is_allow_to_cancel` flag from the list/detail endpoints.',
        '',
        'A `422 SHIPPING_CANCEL_FAILED` is transient — the cancel did not go through and',
        'it is safe to retry by calling the endpoint again.',
      ].join('\n'),
      operationId: 'cancelDelivery',
      security: [{ PlatformSession: [] }],
      parameters: [{ name: 'id', in: 'path', required: true, schema: { type: 'integer' }, description: 'Delivery id. Must belong to the current player.' }],
      responses: {
        '200': {
          description: 'The canceled delivery — same shape as Get a delivery, with `status: canceled` and every card `item_settle_status: canceled`.',
          content: { 'application/json': { schema: { type: 'object', required: ['pool_shipping'], properties: { pool_shipping: { type: 'object', properties: { id: { type: 'integer' }, status: { type: 'string', example: 'canceled' }, is_allow_to_cancel: { type: 'boolean', example: false } } } } }, example: { pool_shipping: { id: 4012, status: 'canceled', is_allow_to_cancel: false } } } },
        },
        '401': err('Missing or invalid player session token.'),
        '404': err('Delivery not found or not owned by the current player.'),
        '422': err('`SHIPPING_NOT_CANCELABLE` — the cancel window is closed (fulfillment already started). `SHIPPING_CANCEL_FAILED` — transient failure, safe to retry with another call.'),
        '500': err('Unexpected error.'),
      },
    },
  },
};
